/**
 * Rückfragen und Hinweise als eigener Dialog im Programm — statt
 * `window.confirm`, `window.prompt` und `window.alert`.
 *
 * Warum: Die Browser-Dialoge können vom Browser unterdrückt werden (nach
 * „keine weiteren Dialoge dieser Seite anzeigen" oder in eingebetteten
 * Ansichten). Dann liefert `window.confirm` still `false` und `window.prompt`
 * still `null` — ein `if (!window.confirm(...)) return` endet einfach, und der
 * Klick auf „Löschen" tut nichts, ohne dass jemand erfährt warum. So ist es
 * beim Löschen von Projektdokumenten beim Kunden passiert.
 *
 * Diese Dialoge hängt der Code selbst in die Seite; unterdrücken kann sie
 * niemand. Der Lint verbietet die Browser-Dialoge im ERP-Frontend
 * (`no-restricted-properties` / `no-restricted-globals` in `.eslintrc.cjs`).
 *
 * Bedienung: Enter = OK, Esc = Abbrechen, Klick neben den Dialog = Abbrechen.
 * Der Fokus steht beim Öffnen im Eingabefeld bzw. auf dem OK-Knopf.
 *
 * ```ts
 * if (!(await bestaetige('Eintrag löschen?', { ok: 'Löschen', gefaehrlich: true }))) return
 * const name = await frage('Name der Gruppe', 'KW …')
 * await hinweis('Das hat nicht geklappt.')
 * ```
 */
import { useEffect, useRef, useState } from 'react'
import { createRoot } from 'react-dom/client'

interface DialogProps {
  text: string
  /** Mit Eingabefeld (frage), sonst nur Knöpfe. */
  eingabe?: { vorgabe: string; pflicht: boolean }
  okText: string
  /** Ohne Abbrechen-Knopf (hinweis). */
  nurOk?: boolean
  gefaehrlich?: boolean
  onOk: (wert: string) => void
  onAbbrechen: () => void
}

function RueckfrageDialog({ text, eingabe, okText, nurOk, gefaehrlich, onOk, onAbbrechen }: DialogProps) {
  const [wert, setWert] = useState(eingabe?.vorgabe ?? '')
  const inputRef = useRef<HTMLInputElement>(null)
  const okRef = useRef<HTMLButtonElement>(null)
  const okGesperrt = !!eingabe?.pflicht && wert.trim() === ''

  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    } else {
      okRef.current?.focus()
    }
  }, [])

  const bestaetigen = () => {
    if (okGesperrt) return
    onOk(wert)
  }

  // Tasten hört der Dialog am `window` in der Capture-Phase ab — also vor
  // allem anderen. Das geteilte `Modal` lauscht am `document` (ebenfalls
  // Capture) und würde sonst bei Esc sich selbst schließen statt der
  // Rückfrage darüber, und seine Fokus-Falle zöge Tab zurück zu sich.
  const aktuell = useRef({ bestaetigen, onAbbrechen })
  aktuell.current = { bestaetigen, onAbbrechen }
  useEffect(() => {
    const onKeyDown = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopImmediatePropagation()
        aktuell.current.onAbbrechen()
      } else if (e.key === 'Enter') {
        e.stopImmediatePropagation()
        // Enter auf dem Abbrechen-Knopf bleibt Abbrechen (natives Klick-Verhalten).
        if ((e.target as HTMLElement | null)?.dataset?.rolle === 'abbrechen') return
        e.preventDefault()
        aktuell.current.bestaetigen()
      } else if (e.key === 'Tab') {
        e.stopImmediatePropagation()
        const panel = panelRef.current
        if (!panel) return
        const items = Array.from(panel.querySelectorAll<HTMLElement>('input, button:not([disabled])'))
        if (items.length === 0) return
        const erstes = items[0]
        const letztes = items[items.length - 1]
        const aktiv = document.activeElement
        if (!panel.contains(aktiv)) {
          e.preventDefault()
          erstes.focus()
        } else if (e.shiftKey && aktiv === erstes) {
          e.preventDefault()
          letztes.focus()
        } else if (!e.shiftKey && aktiv === letztes) {
          e.preventDefault()
          erstes.focus()
        }
      }
    }
    window.addEventListener('keydown', onKeyDown, true)
    return () => window.removeEventListener('keydown', onKeyDown, true)
  }, [])

  // z-[1100] statt des üblichen z-50: Rückfragen kommen auch aus geöffneten
  // Dialogen heraus, und das geteilte `Modal` liegt auf z-[1000].
  return (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/60 p-4"
      onMouseDown={(e) => {
        // Nicht bis zum `document` durchreichen — sonst schließen sich
        // darunterliegende Menüs mit „Klick außerhalb".
        e.stopPropagation()
        if (e.target === e.currentTarget) onAbbrechen()
      }}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        ref={panelRef}
        role={nurOk ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-label={text}
        className="w-full max-w-md rounded-lg border border-border p-5 shadow-lg"
        style={{ background: 'hsl(var(--card))', color: 'hsl(var(--card-foreground))' }}
      >
        <p className="whitespace-pre-line text-sm">{text}</p>
        {eingabe && (
          <input
            ref={inputRef}
            type="text"
            value={wert}
            onChange={(e) => setWert(e.target.value)}
            className="mt-3 h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        )}
        <div className="mt-5 flex justify-end gap-2">
          {!nurOk && (
            <button
              type="button"
              data-rolle="abbrechen"
              onClick={onAbbrechen}
              className="h-9 rounded-md border border-input px-4 text-sm font-medium hover:bg-accent"
              style={{ color: 'hsl(var(--card-foreground))' }}
            >
              Abbrechen
            </button>
          )}
          <button
            ref={okRef}
            type="button"
            onClick={bestaetigen}
            disabled={okGesperrt}
            className={
              'h-9 rounded-md px-4 text-sm font-medium disabled:pointer-events-none disabled:opacity-50 ' +
              (gefaehrlich
                ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'bg-primary text-primary-foreground hover:bg-primary/90')
            }
          >
            {okText}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Hängt den Dialog an `document.body` und räumt ihn beim Schließen wieder ab. */
function oeffne<T>(props: Omit<DialogProps, 'onOk' | 'onAbbrechen'>, ok: (wert: string) => T, abbruch: T): Promise<T> {
  return new Promise<T>((resolve) => {
    const vorherFokus = document.activeElement as HTMLElement | null
    const container = document.createElement('div')
    container.setAttribute('data-dialoge', '')
    document.body.appendChild(container)
    const root = createRoot(container)
    let offen = true
    const schliesse = (ergebnis: T) => {
      if (!offen) return
      offen = false
      // Erst nach dem aktuellen Ereignis abbauen — unmount mitten im
      // React-Handler warnt, und der Aufrufer bekommt das Ergebnis danach.
      setTimeout(() => {
        root.unmount()
        container.remove()
        vorherFokus?.focus?.()
      }, 0)
      resolve(ergebnis)
    }
    root.render(
      <RueckfrageDialog
        {...props}
        onOk={(wert) => schliesse(ok(wert))}
        onAbbrechen={() => schliesse(abbruch)}
      />,
    )
  })
}

/** Ja/Nein-Rückfrage. `true` nur, wenn ausdrücklich bestätigt wurde. */
export function bestaetige(text: string, opts?: { ok?: string; gefaehrlich?: boolean }): Promise<boolean> {
  return oeffne({ text, okText: opts?.ok ?? 'OK', gefaehrlich: opts?.gefaehrlich }, () => true, false)
}

/**
 * Fragt einen Text ab. `null` bei Abbruch — wie `window.prompt`. Mit
 * `pflicht` bleibt OK gesperrt, solange das Feld leer ist. `ok` beschriftet den
 * Knopf mit dem Verb der Handlung („Anlegen", „Kopieren").
 */
export function frage(text: string, vorgabe = '', opts?: { pflicht?: boolean; ok?: string }): Promise<string | null> {
  return oeffne<string | null>(
    { text, okText: opts?.ok ?? 'OK', eingabe: { vorgabe, pflicht: !!opts?.pflicht } },
    (wert) => wert,
    null,
  )
}

/** Reiner Hinweis mit OK-Knopf — Ersatz für `window.alert`. */
export function hinweis(text: string): Promise<void> {
  return oeffne<void>({ text, okText: 'OK', nurOk: true }, () => undefined, undefined)
}
